/**
 * SessionDetailPage — /sessions/:id
 *
 *   GET /api/sessions/:id                      session record (header, info, statistics)
 *   GET /api/sessions/:id/:metric?format=json  skeleton | robotic | biometric | hands | parameter | sessionevent,
 *                                              loaded in parallel with per-metric status (see useSessionMetrics)
 *
 * Layout (EGDL design "SessionDetail"): breadcrumb + title + status + meta strip, live / processing
 * banners, Session info accordion, Metric data workspace (chips, view, timescale, export, sticky
 * replay, combined chart + series panel or per-metric charts), Movement statistics tabs.
 *
 * What the game said (components/session/sessionHeader.js): Results shows the Footer's
 * performance sentence and scoreDetails; Session setup shows the Header (version, platform, how
 * it was started, who played, tracker, mode, adaptive difficulty) and the settings in effect.
 *
 * Clinician-run sessions (sessionAccess.js): a guest session names its guest and no player;
 * "Started by" shows who ran it. Delete (DELETE /api/sessions/:id) is offered where the
 * portal allows it, with an inline confirmation, then returns to /sessions.
 *
 * The Header and Footer are whatever a game sent: each section below the title row renders
 * inside its own error boundary, so data the page cannot show costs that section only, and the
 * title row (breadcrumb, Delete) keeps working (security audit 2026-10-18, M2).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { API_BASE } from './apiConfig';
import { useIsSessionLive } from './useLiveSession';
import { useLinkedPlayers } from './api/useLinkedPlayers';
import {
    Accordion, Banner, Breadcrumb, Button, EmptyState, ErrorBoundary, ErrorState, KVGrid, MetaStrip, Skel, SpecRows, StatusBadge, useIsMobile,
} from './components/ui';
import MetricWorkspace from './components/session/MetricWorkspace';
import MovementStats from './components/session/MovementStats';
import useSessionMetrics from './components/session/useSessionMetrics';
import { CHARTABLE_METRICS, METRICS, METRIC_KEYS, buildTimeline, fmtDuration, metricLabel, toMs } from './seriesExtractors';
import './SessionDetailPage.css';
import { gameLabel } from './games';
import { DeleteConfirm, playerLabel, startedByName, useDeleteSession, useSessionAccess } from './sessionAccess';
import { detailRows, headerRows, settingsRows, settingsSource, show } from './components/session/sessionHeader';

const fmtDateTime = (ms, seconds) => (ms == null ? null : new Date(ms).toLocaleString([], {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', ...(seconds ? { second: '2-digit' } : {}),
}));
const fmtRate = (hz) => (hz >= 10 ? Math.round(hz) : +hz.toFixed(1));
const fmtClock = (ms) => new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/** What the server said for one stream (useSessionMetrics); still loading is unknown, so left out. */
function streamState(key, m) {
    if (!m || m.status === 'loading') return null;
    if (m.status === 'processing') return 'Processing';
    if (m.status === 'empty') return 'None';
    if (m.status === 'failed') return m.code ? `Failed (${m.code})` : 'Failed';
    const unit = key === 'skeleton' ? 'frames' : key === 'parameter' || key === 'sessionevent' ? 'entries' : 'samples';
    return `${m.data.length.toLocaleString()} ${unit}`;
}

/** Spec rows for an empty metric section: the charted streams, then the records (METRICS' chart flag). */
const streamGroups = (metrics) => [true, false].map((chart) => METRICS
    .filter((m) => m.chart === chart)
    .map((m) => ({ label: m.label, value: streamState(m.key, metrics[m.key]) })));
const shortId = (id) => (id ? String(id).slice(-6).toUpperCase() : '');

function LoadingPage() {
    return (
        <div className="eg-page">
            <div className="eg-loading" aria-busy="true" aria-label="Loading session">
                <Skel w={220} h={16} />
                <Skel w="min(420px, 100%)" h={40} />
                <div className="sd-skel-row"><Skel w={140} h={44} /><Skel w={200} h={44} /><Skel w={120} h={44} /></div>
                <Skel h={64} raised />
                <div className="sd-skel-row"><Skel w={140} h={48} /><Skel w={140} h={48} /><Skel w={140} h={48} /><Skel w={140} h={48} /></div>
                <Skel h={440} raised />
                <span className="eg-loading__note">Loading session data…</span>
            </div>
        </div>
    );
}

/** One section that fails to render says so in its place; the rest of the page still works. */
function SectionBoundary({ what, resetKey, children }) {
    return (
        <ErrorBoundary
            resetKey={resetKey}
            fallback={(
                <Banner tone="danger" title={`${what} couldn’t be shown.`} />
            )}
        >
            {children}
        </ErrorBoundary>
    );
}

/** Results: the Footer's performance sentence, score and scoreDetails. */
function GameResults({ results }) {
    const r = results && typeof results === 'object' ? results : {};
    const rows = detailRows(r.customDetails);
    const score = typeof r.score === 'number' ? r.score.toLocaleString() : show(r.score);
    const performance = show(r.performance);
    if (score == null && !performance && !rows.length) return null;
    return (
        <section aria-labelledby="sd-results-h" className="eg-section">
            <div className="eg-section-head">
                <h2 id="sd-results-h" className="eg-h2">Results</h2>
                {score != null ? <span className="eg-body eg-secondary">Score {score}</span> : null}
            </div>
            {performance ? <p className="eg-body sd-performance">{performance}</p> : null}
            {rows.length ? <KVGrid rows={rows} /> : null}
        </section>
    );
}

/** Session setup: the Header and the settings in effect. */
function SessionSetup({ header, nameOf, isMobile, open, onToggle }) {
    const rows = headerRows(header, nameOf);
    const settings = settingsRows(header);
    if (!rows.length && !settings.length) return null;
    const settingsFrom = settingsSource(header);
    const summary = ['Platform', 'Started from', 'Tracker']
        .map((label) => rows.find((r) => r.label === label)?.value)
        .filter(Boolean).join(' · ');
    return (
        <Accordion
            title="Session setup"
            summary={isMobile ? null : summary || 'How the game was set up'}
            open={open}
            onToggle={onToggle}
        >
            {rows.length ? <KVGrid rows={rows} /> : null}
            {settings.length ? (
                <div className="sd-settings">
                    <span className="eg-caps-14 eg-secondary">Settings in effect{settingsFrom ? ` · ${settingsFrom}` : ''}</span>
                    <KVGrid rows={settings} />
                </div>
            ) : null}
        </Accordion>
    );
}

function SessionDetailPage() {
    const { id } = useParams();
    const { authFetch, user } = useAuth();
    const isProvider = user?.role === 'provider';
    const { nameOf: linkedName } = useLinkedPlayers();
    // a name is text or nothing: it lands in the breadcrumb, the meta strip and the header rows
    const nameOf = (uid) => {
        const name = linkedName(uid);
        return typeof name === 'string' && name ? name : null;
    };
    const isLive = useIsSessionLive(id);
    const isMobile = useIsMobile();
    const navigate = useNavigate();
    const { canDelete } = useSessionAccess();

    /* ── session record ──────────────────────────────────────────────── */
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null); // { code, message }
    const [checkedAt, setCheckedAt] = useState(null);
    const [checking, setChecking] = useState(false);
    const [infoOpen, setInfoOpen] = useState(false);
    const [setupOpen, setSetupOpen] = useState(false);

    const loadSession = useCallback(async (quiet) => {
        if (!quiet) { setLoading(true); setError(null); }
        try {
            const res = await authFetch(`${API_BASE}/api/sessions/${id}`);
            if (!res.ok) {
                let message = '';
                try { message = (await res.json())?.error || ''; } catch { /* non-JSON error body */ }
                throw Object.assign(new Error(message || `Session didn’t load (${res.status}).`), { code: res.status });
            }
            setSession(await res.json());
            setError(null);
            setCheckedAt(Date.now());
        } catch (e) {
            if (!quiet) setError({ code: e.code || 0, message: e.message || 'Session didn’t load.' });
        } finally {
            if (!quiet) setLoading(false);
        }
    }, [authFetch, id]);
    useEffect(() => { loadSession(false); }, [loadSession]);

    /* ── metrics ─────────────────────────────────────────────────────── */
    const { metrics, settled, loadAll, retry } = useSessionMetrics(id, authFetch);
    const okData = useMemo(() => {
        const d = {};
        METRIC_KEYS.forEach((k) => { if (metrics[k]?.status === 'ok') d[k] = metrics[k].data; });
        return d;
    }, [metrics]);
    const timeline = useMemo(() => buildTimeline(okData, session), [okData, session]);

    const checkAgain = async () => {
        setChecking(true);
        await loadSession(true);
        await loadAll();
        setChecking(false);
    };

    const del = useDeleteSession(session || { _id: id }, () => navigate('/sessions', { replace: true }));

    if (loading) return <LoadingPage />;

    if (error || !session) {
        const notFound = error?.code === 404;
        return (
            <div className="eg-page">
                <Breadcrumb items={[{ label: 'Sessions', to: '/sessions' }, { label: `Session ${shortId(id)}` }]} />
                {notFound ? (
                    <EmptyState title="Session not found" action={<Button to="/sessions">Back to sessions</Button>} />
                ) : (
                    <ErrorState title="Session didn’t load" onRetry={() => loadSession(false)}>
                        {error?.message || 'The server returned an error.'} Try again in a moment.
                    </ErrorState>
                )}
            </div>
        );
    }

    /* ── header data ─────────────────────────────────────────────────── */
    const start = toMs(session.startTime);
    const end = toMs(session.endTime);
    const lengthMs = toMs(session.results?.length);
    const durationMs = start != null && end != null && end > start ? end - start : lengthMs > 0 ? lengthMs : null;
    // a guest session is the clinician's own: it names the guest, never the clinician as player
    const playerName = session.guest ? playerLabel(session, nameOf) : nameOf(session.user_id);
    const startedBy = startedByName(session, user, nameOf);
    const status = session.consolidated || null;
    const isProcessing = !!status && status !== 'complete' && !isLive; // the live banner covers a running game

    const crumbs = [
        { label: 'Sessions', to: '/sessions' },
        ...(isProvider && playerName && !session.guest ? [{ label: playerName, to: `/players/${session.user_id}` }] : []),
        { label: `Session ${shortId(session._id || id)}` },
    ];
    // Session info's summary: its facts, in the "·" style of the Session setup summary
    const span = start == null ? null : end != null && end > start ? `${fmtClock(start)}–${fmtClock(end)}` : fmtClock(start);
    const infoSummary = [show(session.location), span].filter(Boolean).join(' · ') || null;
    const meta = [
        session.version && { label: 'Version', value: session.version },
        start != null && { label: 'Date', value: fmtDateTime(start) },
        durationMs != null && { label: 'Duration', value: fmtDuration(durationMs) },
        playerName && { label: 'Player', value: playerName },
        startedBy && { label: 'Started by', value: startedBy },
        session.offline === true && { label: 'Played', value: 'Offline' },
    ];
    // played offline and uploaded later (egdl_Core GAMES-OFFLINE-SPEC.md §4): when it came, and what it continues
    const uploaded = session.offline === true && typeof session.uploaded_at === 'number' ? toMs(session.uploaded_at) : null;
    const continues = session.offline === true && typeof session.continues === 'string' && /^[0-9a-f]{24}$/i.test(session.continues) ? session.continues : null;

    // Densest loaded stream, for the "samples" note and the info grid.
    const rateOf = (k) => timeline?.rates?.[k];
    const dense = CHARTABLE_METRICS.filter((k) => okData[k]).sort((a, b) => okData[b].length - okData[a].length)[0];
    let sampleNote = '';
    if (dense) {
        const what = dense === 'skeleton' ? 'skeleton frames' : `${metricLabel(dense).toLowerCase()} samples`;
        sampleNote = `${okData[dense].length.toLocaleString()} ${what}${rateOf(dense) ? ` · ${fmtRate(rateOf(dense))} Hz` : ''}`;
    } else if (settled && METRIC_KEYS.every((k) => metrics[k]?.status === 'empty')) {
        sampleNote = '0 samples';
    }

    const info = [
        { label: 'Session ID', value: session._id || id },
        isProvider && session.user_id && !session.guest ? { label: 'User ID', value: playerName ? `${session.user_id} · ${playerName}` : session.user_id } : null,
        session.guest ? { label: 'Guest', value: session.guest_label || 'No name given' } : null,
        startedBy ? { label: 'Started by', value: startedBy } : null,
        session.game ? { label: 'Game', value: gameLabel(session.game) } : null,
        session.version ? { label: 'Version', value: session.version } : null,
        session.location ? { label: 'Location', value: session.location } : null,
        session.type || session.env ? { label: 'Type', value: [show(session.type), show(session.env)].filter(Boolean).join(' · ') } : null,
        start != null ? { label: 'Started', value: fmtDateTime(start, true) } : null,
        end != null ? { label: 'Ended', value: fmtDateTime(end, true) } : null,
        session.offline === true ? { label: 'Played', value: 'Offline' } : null,
        uploaded != null ? { label: 'Uploaded', value: fmtDateTime(uploaded, true) } : null,
        continues ? { label: 'Continues', value: <Link to={`/sessions/${continues}`}>{`Session ${shortId(continues)}`}</Link> } : null,
        okData.skeleton ? { label: 'Skeleton samples', value: `${okData.skeleton.length.toLocaleString()} frames${rateOf('skeleton') ? ` · ${fmtRate(rateOf('skeleton'))} Hz` : ''}` } : null,
    ].filter(Boolean);

    /* ── metric section state ────────────────────────────────────────── */
    const keysWith = (s) => METRIC_KEYS.filter((k) => metrics[k]?.status === s);
    const okKeys = keysWith('ok');
    const failedKeys = keysWith('failed');
    const processingKeys = keysWith('processing');
    let metricBody;
    if (!settled) {
        metricBody = (
            <div className="eg-loading" aria-busy="true">
                <div className="sd-skel-row">{METRIC_KEYS.map((k) => <Skel key={k} w={140} h={48} />)}</div>
                <Skel h={440} raised />
                <span className="eg-loading__note">Loading metric data…</span>
            </div>
        );
    } else if (!okKeys.length && processingKeys.length) {
        metricBody = (
            <EmptyState title="Metric data isn’t ready yet" details={<SpecRows groups={streamGroups(metrics)} />} />
        );
    } else if (!okKeys.length && failedKeys.length) {
        const codes = [...new Set(failedKeys.map((k) => metrics[k].code).filter(Boolean))];
        metricBody = (
            <ErrorState title="Session data didn’t load" onRetry={loadAll}>
                The data service returned an error{codes.length ? ` (${codes.join(', ')})` : ''}.
            </ErrorState>
        );
    } else if (!okKeys.length || !timeline) {
        metricBody = (
            <EmptyState title="No metric data recorded" details={<SpecRows groups={streamGroups(metrics)} />} />
        );
    } else {
        metricBody = <MetricWorkspace key={id} id={id} metrics={metrics} okData={okData} timeline={timeline} retry={retry} />;
    }

    const lastChecked = checkedAt ? new Date(checkedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : null;

    return (
        <div className="eg-page sd-page">
            <header className="eg-page-head">
                <Breadcrumb items={crumbs} />
                <div className="eg-title-row">
                    <h1 className="eg-h1">{session.game ? gameLabel(session.game) : 'Untitled game'}</h1>
                    {status ? <StatusBadge status={status} large /> : null}
                    {canDelete(session) ? (
                        <Button variant="ghost" muted className="sa-delete" aria-expanded={del.asking} onClick={del.ask}>Delete session</Button>
                    ) : null}
                </div>
                <MetaStrip items={meta} />
            </header>

            <DeleteConfirm del={del} />

            {isLive ? (
                <Banner
                    tone="info"
                    title="This session is live right now."
                    action={<Button variant="primary" size="sm" to={`/live/${id}`} icon="live">Open live view</Button>}
                />
            ) : null}

            {isProcessing ? (
                <Banner
                    pulse
                    title={status === 'pending' ? 'Still processing' : 'Not consolidated yet'}
                    action={<Button onClick={checkAgain} disabled={checking}>{checking ? 'Checking…' : 'Check again'}</Button>}
                >
                    {lastChecked ? `Last checked ${lastChecked}` : ''}
                </Banner>
            ) : null}

            <SectionBoundary what="Results" resetKey={id}>
                <GameResults results={session.results} />
            </SectionBoundary>

            <Accordion
                title="Session info"
                summary={isMobile ? null : infoSummary}
                open={infoOpen}
                onToggle={() => setInfoOpen((o) => !o)}
            >
                <SectionBoundary what="Session info" resetKey={id}>
                    <KVGrid rows={info} />
                </SectionBoundary>
            </Accordion>

            <SectionBoundary what="Session setup" resetKey={id}>
                <SessionSetup
                    header={session.header}
                    nameOf={nameOf}
                    isMobile={isMobile}
                    open={setupOpen}
                    onToggle={() => setSetupOpen((o) => !o)}
                />
            </SectionBoundary>

            <section aria-labelledby="sd-metric-h" className="eg-section">
                <div className="eg-section-head">
                    <h2 id="sd-metric-h" className="eg-h2">Metric data</h2>
                    {sampleNote ? <span className="eg-body eg-secondary">{sampleNote}</span> : null}
                </div>
                <SectionBoundary what="Metric data" resetKey={id}>
                    {metricBody}
                </SectionBoundary>
            </section>

            <SectionBoundary what="Movement statistics" resetKey={id}>
                <MovementStats results={session.results} skeleton={okData.skeleton} />
            </SectionBoundary>
        </div>
    );
}

export default SessionDetailPage;
